import React, { useCallback, useState } from 'react';
import Plot from '../../../node_modules/react-plotly.js/react-plotly';

import { Divider, Row, Col, Space, Typography } from 'antd';
import usePlotlyAnnotations from "./usePlotlyAnnotations";
import { toRGB } from "../../utils/colorUtils";
import Button from "@mui/material/Button";
import { COLD_PALETTE } from "../../utils/colorUtils";
import { EnvironmentTwoTone } from '@ant-design/icons';
// Helper function to calculate percentage change
const calculatePercentageChange = (data, normalize) => {
    if (!normalize) return data;  // Return raw data if not normalizing
    const baseValue = data[0][1]; // First value of the time series
    return data.map(([date, value]) => [date, ((value - baseValue) / baseValue)]);
};


const extractDateRange = (dates) => {
    if (!dates || dates.length === 0) return undefined;
    if (dates.length === 1) return [dates[0], undefined];
    const sortedDates = dates.sort((a, b) => new Date(a) - new Date(b));
    return [sortedDates[0], sortedDates[sortedDates.length - 1]];

}

// Generalized X and Y axis configurations
const getXAxisConfig = (disableScrollbar, title = '', fontSize = 11, range = null) => ({
    rangeslider: { visible: !disableScrollbar },
    showspikes: true,
    spikethickness: 1,
    spikedash: 'solid',
    spikemode: 'across',
    zeroline: false,
    spikedistance: -1,
    showline: false,  // Remove the x-axis line

    title,
    tickfont: { size: fontSize },
    range: extractDateRange(range),  // Preserve range if provided
});


const getYAxisConfig = (minY, maxY, valueFormat, title = '', fontSize = 11) => ({
    title,
    tickformat: valueFormat,  // Dollar format or percentage based on valueFormat
    showspikes: true,  // Show spikes on the y-axis
    spikedthickness: 1,
    zerolinecolor: '#eeefff',
    spikedash: 'solid',
    spikemode: 'across',  // Spikes across the whole chart
    spikedistance: -1,  // Show spikes at any point
    range: (minY !== undefined && maxY !== undefined) ? [minY, maxY] : undefined,  // Set min/max range if provided
    tickfont: {
        size: fontSize,
    }
});

/**
 * PlotlyStockChart
 *
 * @param {Object[]} timeseriesData - Array of time series data objects. Each object represents a series.
 * @param {string} timeseriesData[].name - Name of the time series (e.g., "Portfolio").
 * @param {Array[]} timeseriesData[].data - Array of data points. Each data point is an array of the form [timestamp, value].
 * @param {string} timeseriesData[].color - Optional. Color of the series line.
 * @param {boolean} timeseriesData[].area - Optional. If true, the line will be displayed as an area chart (filled underneath).
 * @param {string} timeseriesData[].fill - Optional. Defines how the area under the line should be filled (e.g., "tozeroy").
 *
 * @param {function} onDateSelected - Callback for when a date is selected (annotation added).
 * @param {function} onDateRemoved - Callback for when a date annotation is removed.
 * @param {function} callbackOnClearAnnotations - Callback for when all annotations are cleared.
 * @param {JSX.Element} extra - Additional JSX elements (e.g., extra buttons).
 * @param {string} description - Optional. Chart description or title.
 * @param {string} xTitle - Optional. X-axis title.
 * @param {string} yTitle - Optional. Y-axis title.
 * @param {boolean} disableAnnotations - Optional. Disable annotation interactions (adding/removing).
 * @param {number} fontSize - Optional. Font size for the chart labels.
 * @param {boolean} disableScrollbar - Optional. Disable the X-axis range slider.
 * @param {Array} shadedRegion - Optional. Specifies regions to shade under the chart.
 * @param {Array<string>} verticalLines - Optional. Dates where vertical lines should be drawn on the chart.
 * @param {Array<string>} verticalLineLabels - Optional. Labels for the vertical lines
 * @param {number} minY - Optional. Minimum Y-axis value.
 * @param {number} maxY - Optional. Maximum Y-axis value.
 * @param {boolean} normalize - Optional. Normalize the time series data to show percentage change. Defaults to true.
 * @param {string} valueFormat - Optional. Format for the Y-axis values (e.g., ".2%" for percentage, "$" for dollar values).
 *
 * @example
 * const timeseriesData = [
 *   {
 *     name: "Portfolio",
 *     data: [
 *       ["2023-01-01", 150000],  // Timestamp and value
 *       ["2023-01-02", 151000],
 *       // More data points...
 *     ],
 *     color: "#0083ff",  // Color of the line
 *     area: true,  // Display as area chart
 *     fill: 'tozeroy',  // Fill the area below the line
 *   },
 *   {
 *     name: "Benchmark",
 *     data: [
 *       ["2023-01-01", 4000],
 *       ["2023-01-02", 4050],
 *       // More data points...
 *     ],
 *     color: "#4d5d70",  // Color of the benchmark line
 *     area: false,  // Not an area chart, just a line
 *   },
 * ];
 *
 * @returns {JSX.Element}
 */
const PlotlyStockChart = ({
    timeseriesData,
    onDateSelected,
    onDateRemoved,
    callbackOnClearAnnotations,
    extra,
    description = '',  // Change title to description
    xTitle = '',  // Optional X-axis title
    yTitle = '',  // Optional Y-axis title
    disableAnnotations = false,
    disableToolbar = false,  // Prop to control toolbar
    disableGrid = false,
    annotationColor = '#A790EB',
    disableScrollbar = false,  // Prop to control scrollbar
    shadedRegion = null,  // Optional shaded region
    verticalLines = [],  // List of dates to place vertical lines
    verticalLineLabels = [],  // List of labels for the vertical lines
    minY,  // Optional minY for Y-axis
    maxY,  // Optional maxY for Y-axis
    normalize = true,  // Normalize data by default
    valueFormat = '.2%',  // Optional value format (e.g., '$' for dollar formatting)\
}) => {
    // Use the custom hook to manage annotations and shapes
    const {
        annotations, shapes, handleAnnotationClick,
        removeAllAnnotations
    } = usePlotlyAnnotations({
        onDateSelected,
        onDateRemoved,
        initialVerticalLines: verticalLines,  // Pass vertical lines to the hook
        initialVerticalLineLabels: verticalLineLabels,  // Pass vertical line labels to the hook
        shadedRegion,
        annotationColor,

    });
    const [selectedRange, setSelectedRange] = useState(null)
    // Prepare the data for Plotly in percentage mode, handling 'color' and 'area'
    const plotData = timeseriesData.map((series, index) => {
        const percentageData = calculatePercentageChange(series.data, normalize);
        const defaultColor = COLD_PALETTE[index % COLD_PALETTE.length];
        return {
            x: percentageData.map(row => row[0]),  // Extracting the timestamps
            y: percentageData.map(row => row[1]),  // Extracting the percentage change
            // type: "scatter",
            mode: "lines+markers",
            marker: {
                symbol: "circle",  // <--- circle instead of square or dash
                size: 1
            },
            fill: series.area ? "tozeroy" : null,  // Fill area under the line if area=true
            fillcolor: series.area && toRGB(series.color, series?.areaOpacity ?? 0.3) || toRGB(defaultColor, 0.06),  // Use the 'color' field if present
            line: {
                color: series.color || defaultColor,
                thickness: 1,
            },
            name: series.name,
        };
    });
    const handleRelayout = (eventData) => {
        if (eventData["xaxis.range"]) {
            const startDate = eventData["xaxis.range"][0];
            const endDate = eventData["xaxis.range"][1];
            setSelectedRange([startDate, endDate]);
        }
    };

    const handleClick = useCallback((event) => {
        if (disableAnnotations || !event.points || !event.points.length) return;  // Ensure points exist and annotations are enabled
        const xValue = event.points[0].x; // Extract the x (date) value from the clicked point
        handleAnnotationClick(xValue); // Use the hook's click handler
    }, [handleAnnotationClick, disableAnnotations]);


    const handleRemoveAllAnnotations = () => {
        removeAllAnnotations();
        callbackOnClearAnnotations?.();
    }

    return (
        <div style={{ width: '100%', height: '100%' }}>
            {!disableToolbar && (
                <Space direction={'vertical'}>
                    {description && (
                        <Typography.Text style={{ fontSize: '14px' }}>{description}</Typography.Text>
                    )}
                    {!disableAnnotations && (
                        <Space>
                            <Button variant={'outlined'} color={'error'} onClick={handleRemoveAllAnnotations}>
                                Clear Annotations
                            </Button>
                            {extra}
                        </Space>
                    )}
                </Space>
            )}
            <Plot
                data={plotData}
                layout={{
                    annotations: disableAnnotations ? [] : annotations,
                    shapes: disableAnnotations ? [] : shapes,
                    hovermode: "x unified",
                    yaxis: getYAxisConfig(minY, maxY, valueFormat, yTitle),
                    xaxis: getXAxisConfig(disableScrollbar, xTitle, 11, selectedRange),
                    dragmode: "zoom",
                    responsive: true,
                    // Tweak margins so there's less white space on the right
                    margin: { l: 50, r: 20, t: 40, b: 50 },
                    legend: {
                        orientation: 'h',   // Horizontal legend
                        x: 0,               // Start at left edge
                        xanchor: 'left',
                        y: 1,               // Float legend near top
                        yanchor: 'top',
                        // Use circles in the legend
                        // (Although each trace’s "marker.symbol" can override this if set)
                        itemsizing: 'constant',
                        itemclick: 'toggle',
                        itemdoubleclick: 'toggleothers',
                    },
                    font: {
                        family: 'Quicksand',
                        color: '#000',
                        weight: 400,
                    },
                }}

                useResizeHandler={true}  // Make the chart responsive to container size
                style={{ width: "100%", height: "100%" }}  // Make the chart fill the container
                onRelayout={handleRelayout}  // Capture range selection events
                onClick={handleClick}  // Capture click events
                config={{ displayModeBar: false }}  // Hide the toolbar
                disableGrid={disableGrid}

            />
        </div>
    );
};

export default PlotlyStockChart;